import { motion } from "framer-motion";
import { colors, fonts } from "@/lib/theme";

interface HeaderProps {
  title?: string | null;
  description?: string | null;
  exhibitNumber?: number;
}

/**
 * Option 4: Minimal with Growing Vertical Lines
 * Clean vertical lines that grow with accent highlights
 */
export function MinimalLinesHeader({ title, description, exhibitNumber }: HeaderProps) {
  const hasContent = title || description;

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-8 relative overflow-hidden"
      style={{ backgroundColor: colors.surface }}
    >
      {/* Left vertical line - closer to edge on mobile */}
      <motion.div
        initial={{ height: 0 }}
        whileInView={{ height: "100%" }}
        viewport={{ once: false, amount: 0.3 }}
        transition={{ duration: 1.2, ease: [0.4, 0, 0.2, 1] }}
        className="absolute w-px top-0 left-[8%] md:left-[20%]"
        style={{ backgroundColor: colors.border }}
      />

      {/* Right vertical line - closer to edge on mobile */}
      <motion.div
        initial={{ height: 0 }}
        whileInView={{ height: "100%" }}
        viewport={{ once: false, amount: 0.3 }}
        transition={{ duration: 1.2, ease: [0.4, 0, 0.2, 1] }}
        className="absolute w-px bottom-0 right-[8%] md:right-[20%]"
        style={{ backgroundColor: colors.border }}
      />

      {/* Left accent line */}
      <motion.div
        initial={{ height: 0 }}
        whileInView={{ height: "25%" }}
        viewport={{ once: false, amount: 0.3 }}
        transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1], delay: 0.4 }}
        className="absolute w-px top-[20%] left-[calc(8%+6px)] md:left-[calc(20%+8px)]"
        style={{ backgroundColor: colors.primary }}
      />

      {/* Right accent line */}
      <motion.div
        initial={{ height: 0 }}
        whileInView={{ height: "25%" }}
        viewport={{ once: false, amount: 0.3 }}
        transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1], delay: 0.4 }}
        className="absolute w-px bottom-[20%] right-[calc(8%+6px)] md:right-[calc(20%+8px)]"
        style={{ backgroundColor: colors.primary }}
      />

      {/* Content */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: false, amount: 0.3 }}
        transition={{ duration: 0.8, delay: 0.6 }}
        className="text-center z-10 max-w-2xl"
      >
        {exhibitNumber && (
          <div
            style={{
              fontFamily: fonts.sans,
              fontSize: "0.75rem",
              letterSpacing: "0.2em",
              color: colors.primary,
              marginBottom: "1rem",
              textTransform: "uppercase",
            }}
          >
            Exhibit {exhibitNumber}
          </div>
        )}

        {title && (
          <h2
            className="text-2xl sm:text-3xl md:text-5xl mb-4 md:mb-6"
            style={{
              fontFamily: fonts.serif,
              color: colors.text,
              fontWeight: 400,
              letterSpacing: "0.02em",
            }}
          >
            {title}
          </h2>
        )}

        {/* Divider */}
        <div
          className="mx-auto mb-4 md:mb-6"
          style={{
            width: 40,
            height: 1,
            backgroundColor: colors.accent,
          }}
        />

        {description && (
          <p
            className="text-base md:text-lg"
            style={{
              fontFamily: fonts.sans,
              color: colors.textMuted,
              lineHeight: 1.8,
            }}
          >
            {description}
          </p>
        )}

        {!hasContent && (
          <div
            style={{
              width: 60,
              height: 2,
              backgroundColor: colors.primaryMuted,
              margin: "0 auto",
            }}
          />
        )}
      </motion.div>
    </div>
  );
}
